import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, warehousesApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Package,
  Warehouse,
  Truck,
  ShoppingCart,
  AlertTriangle,
  ArrowLeftRight,
  DollarSign,
  TrendingUp,
  ArrowRight,
  Clock
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-900 text-white',
    orange: 'bg-orange-500 text-white',
    emerald: 'bg-emerald-500 text-white',
    red: 'bg-red-500 text-white',
    slate: 'bg-slate-700 text-white'
  };

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-2 tabular-nums">{value}</p>
            {trend && (
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                {trend}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-sm ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentMovements, setRecentMovements] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, movementsRes, ordersRes, alertsRes] = await Promise.all([
          dashboardApi.getStats(),
          dashboardApi.getRecentMovements(5),
          dashboardApi.getPendingOrders(5),
          warehousesApi.getLowStockAlerts()
        ]);
        
        setStats(statsRes.data);
        setRecentMovements(movementsRes.data);
        setPendingOrders(ordersRes.data);
        setLowStockAlerts(alertsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getMovementTypeBadge = (type) => {
    const types = {
      IN: { label: 'Entrada', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      OUT: { label: 'Salida', className: 'bg-red-50 text-red-700 border-red-200' },
      ADJUST: { label: 'Ajuste', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      TRANSFER: { label: 'Transfer', className: 'bg-blue-50 text-blue-700 border-blue-200' }
    };
    return types[type] || { label: type, className: 'bg-slate-100 text-slate-700' };
  };

  const getStatusBadge = (status) => {
    const statuses = {
      SENT: { label: 'Enviada', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      PARTIALLY_RECEIVED: { label: 'Parcial', className: 'bg-amber-50 text-amber-700 border-amber-200' }
    };
    return statuses[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
        <p className="text-slate-500 mt-1">Resumen general del sistema de inventarios</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Productos"
          value={stats?.total_products || 0}
          icon={Package}
          color="blue"
        />
        <StatCard
          title="Almacenes"
          value={stats?.total_warehouses || 0}
          icon={Warehouse}
          color="slate"
        />
        <StatCard
          title="Proveedores"
          value={stats?.total_suppliers || 0}
          icon={Truck}
          color="emerald"
        />
        <StatCard
          title="Valor Inventario"
          value={formatCurrency(stats?.total_stock_value || 0)}
          icon={DollarSign}
          color="orange"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-sm bg-amber-500">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-amber-700">Stock Bajo</p>
              <p className="text-2xl font-bold text-amber-900">{stats?.low_stock_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-sm bg-blue-900">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-700">Órdenes Pendientes</p>
              <p className="text-2xl font-bold text-blue-900">{stats?.pending_orders || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-sm bg-slate-700">
              <ArrowLeftRight className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">Movimientos (24h)</p>
              <p className="text-2xl font-bold text-slate-900">{stats?.recent_movements || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Movements */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-semibold">Movimientos Recientes</CardTitle>
            <Link 
              to="/movements" 
              className="text-sm text-blue-900 hover:underline flex items-center gap-1"
            >
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentMovements.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No hay movimientos recientes</p>
            ) : (
              <div className="space-y-3">
                {recentMovements.map((mov, idx) => {
                  const badge = getMovementTypeBadge(mov.movement_type);
                  return (
                    <div 
                      key={mov.id || idx} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-sm hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Badge className={badge.className}>{badge.label}</Badge>
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {mov.lines?.[0]?.product_name || 'Producto'}
                          </p>
                          <p className="text-xs text-slate-500">{mov.reference || 'Sin referencia'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-slate-900 tabular-nums">
                          {mov.lines?.[0]?.quantity || 0}
                        </p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(mov.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low Stock Alerts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-semibold">Alertas de Stock Bajo</CardTitle>
            <Link 
              to="/mrp" 
              className="text-sm text-blue-900 hover:underline flex items-center gap-1"
            >
              Ver MRP <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {lowStockAlerts.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                  <Package className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-sm text-slate-500">No hay alertas de stock bajo</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lowStockAlerts.map((alert, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-red-50 rounded-sm border border-red-100"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">{alert.product_name}</p>
                      <p className="text-xs text-slate-500">{alert.warehouse_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-red-600 tabular-nums">
                        {alert.current_stock} / {alert.stock_min}
                      </p>
                      <p className="text-xs text-red-500">Déficit: {alert.deficit}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pending Orders */}
      {pendingOrders.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-semibold">Órdenes de Compra Pendientes</CardTitle>
            <Link 
              to="/purchase-orders" 
              className="text-sm text-blue-900 hover:underline flex items-center gap-1"
            >
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                      # Orden
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                      Proveedor
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                      Estado
                    </th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOrders.map((order, idx) => {
                    const badge = getStatusBadge(order.status);
                    return (
                      <tr key={order.id || idx} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm font-medium text-blue-900">
                          {order.order_number}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-900">
                          {order.supplier_name || 'Proveedor'}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={badge.className}>{badge.label}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500 text-right tabular-nums">
                          {new Date(order.created_at).toLocaleDateString('es-MX')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
